/**
 * Canvas text label placement: hover ghost, click-to-place hugging label, or
 * drag-to-width fixed label.
 * Uses window listeners so drag release is visible outside the pane element.
 */

import { Node, useReactFlow } from "@xyflow/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CANVAS_SNAP, snapToGrid } from "../canvas-controls.config";
import { useCanvas } from "../context/CanvasContext";
import { attachNodeToSectionIfApplicable } from "../utils/sectionMembership";
import { readLabelStyle } from "./useCanvasLabelStyle";

const MIN_DRAG_WIDTH = 40; // minimum drag distance before we use dragged width

interface Position {
  x: number;
  y: number;
}

export interface TextPlacementGhost {
  position: Position;
  /** Set while drag is in progress — represents the current drag width in flow coords */
  dragWidth?: number;
}

/**
 * Manages canvas text node placement with click-to-place and click-drag-to-size support.
 * Uses window-level events so mousedown/mouseup are available (ReactFlow only exposes
 * onPaneClick which fires only on non-drag clicks).
 */
export function useTextLabelPlacement() {
  const reactFlowInstance = useReactFlow();
  const { screenToFlowPosition } = reactFlowInstance;
  const { addNode } = useCanvas();
  const [isPlacingTextLabel, setIsPlacingTextLabel] = useState(false);
  const [ghost, setGhost] = useState<TextPlacementGhost | null>(null);

  const screenToFlowRef = useRef(screenToFlowPosition);
  screenToFlowRef.current = screenToFlowPosition;
  const reactFlowInstanceRef = useRef(reactFlowInstance);
  reactFlowInstanceRef.current = reactFlowInstance;

  const handleToggleTextLabelMode = useCallback(() => {
    setIsPlacingTextLabel((prev) => !prev);
  }, []);

  useEffect(() => {
    if (!isPlacingTextLabel) {
      setGhost(null);
      return;
    }

    // Tracks where the current drag started (null when not dragging)
    let dragStart: Position | null = null;

    const getFlow = (e: MouseEvent): Position =>
      screenToFlowRef.current({ x: e.clientX, y: e.clientY });

    /** Returns true when the event target is on the ReactFlow pane (not a node or toolbar) */
    const isOnPane = (e: MouseEvent): boolean => {
      const target = e.target as HTMLElement;
      if (target.closest("[data-canvas-control]")) return false;
      return !!target.closest(".react-flow__pane");
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setIsPlacingTextLabel(false);
        setGhost(null);
        dragStart = null;
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      if (e.button !== 0 || !isOnPane(e)) return;
      dragStart = getFlow(e);
    };

    const onMouseMove = (e: MouseEvent) => {
      const pos = getFlow(e);
      if (dragStart && e.buttons === 1) {
        // Active drag — show resizing ghost rectangle (anchor snapped to grid)
        const [gridX, gridY] = CANVAS_SNAP.grid;
        const rawW = pos.x - dragStart.x;
        const dragWidth = Math.max(MIN_DRAG_WIDTH, rawW);
        setGhost({
          position: {
            x: snapToGrid(dragStart.x, gridX),
            y: snapToGrid(dragStart.y, gridY),
          },
          dragWidth,
        });
      } else {
        // Hover — the ghost sits exactly where the node will land, snapped the
        // same way the drop is, so nothing shifts between preview and label.
        const [gridX, gridY] = CANVAS_SNAP.grid;
        setGhost({
          position: {
            x: snapToGrid(pos.x, gridX),
            y: snapToGrid(pos.y, gridY),
          },
        });
      }
    };

    const onMouseUp = (e: MouseEvent) => {
      if (e.button !== 0) return;
      if (!isOnPane(e) || !dragStart) {
        dragStart = null;
        return;
      }

      const pos = getFlow(e);
      const dragDelta = pos.x - dragStart.x;
      // Drag sizes the label explicitly (lattice width, text wraps to it);
      // a plain click leaves the width off entirely so the box hugs the text.
      // Height is never set either way.
      const width =
        Math.abs(dragDelta) >= MIN_DRAG_WIDTH
          ? Math.max(96, snapToGrid(Math.abs(dragDelta), CANVAS_SNAP.grid[0]))
          : undefined;

      const [gridX, gridY] = CANVAS_SNAP.grid;
      const rawX = dragDelta >= 0 ? dragStart.x : pos.x;
      const rawY = dragStart.y;

      const placementStyle = readLabelStyle();
      const newNode: Node = {
        id: uuidv4(),
        type: "canvasText",
        position: {
          x: snapToGrid(rawX, gridX),
          y: snapToGrid(rawY, gridY),
        },
        ...(width !== undefined ? { width } : {}),
        // A new label wears the formatting the user last chose.
        data: {
          content: "",
          font_family: placementStyle.font_family,
          font_size: placementStyle.font_size,
          text_color: placementStyle.text_color,
          is_bold: placementStyle.is_bold,
          is_italic: placementStyle.is_italic,
          is_underline: placementStyle.is_underline,
        },
      };

      const rf = reactFlowInstanceRef.current;
      const attached = attachNodeToSectionIfApplicable({
        node: newNode,
        nodes: rf.getNodes(),
        getInternalNode: (id) => rf.getInternalNode(id),
      });

      addNode({ ...attached, selected: true });
      setIsPlacingTextLabel(false);
      setGhost(null);
      dragStart = null;
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("mousedown", onMouseDown);
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("mousedown", onMouseDown);
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
    };
  }, [isPlacingTextLabel, addNode, reactFlowInstance]);

  return {
    isPlacingTextLabel,
    setIsPlacingTextLabel,
    ghost,
    handleToggleTextLabelMode,
  };
}
